// import firebase from '../Firebase.js';
import * as Sentry from "@sentry/react";
import produce from "immer";
import { isUndefined, omitBy } from "lodash";
import Queue from "queue-promise";
import { arrayMove } from "react-sortable-hoc";
import { createPOVTenseObject, DEFAULT_POV_TENSE_OBJECT, DEFAULT_TONE, DEFAULT_WRITE_MODE, TONES, WRITE_MODES } from "../../../src/shared/constants";
import { normalizeIsMultiplePOVState } from "../../../src/shared/utils/isMultiplePOVState";
import Auth from "../Auth";
import { db } from "../Firebase";
import POVTense from "../models/POVTense";
import Subscription from "./Subscription";

const collection = db.collection("projects");
const EXPORT_JOB_POLL_INTERVAL_MS = 2000;
const EXPORT_JOB_TIMEOUT_MS = 10 * 60 * 1000;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export class ExportCancelledError extends Error {
  constructor(message = "Export cancelled") {
    super(message);
    this.name = "ExportCancelledError";
    this.isExportCancelled = true;
  }
}

export default class Project {
  #snapshotInitialised = false;

  constructor({ id, data, metaData }) {
    if (!id) {
      throw new Error("Must specify an id");
    }
    if (!data) {
      throw new Error("Must specify data");
    }

    this.id = id;
    this.fetched = false;
    this.queue = new Queue({ concurrent: 1, interval: 1, start: true });
    this.automaticDocumentTitlingMemo = {};
    this.automaticProjectTitlingMemo = undefined;
    this.creatingDocument = false;
    this._pendingOptimisticDeletes = 0;
    this._pendingTitleSaves = 0;
    this._pendingImportDismissalSaves = 0;
    this._pendingFolderCollapseSaves = 0;

    if (data) {
      this.setData({ data: data, metaData: metaData });
    } else {
      this.data = {};
    }
  }

  get title() {
    return this.data.title;
  }

  get activeDocument() {
    return this.data.activeDocument;
  }

  get tone() {
    return TONES.find((t) => t.value === this.data.tone) || DEFAULT_TONE;
  }

  get memory() {
    return this.data.memory;
  }

  get memoryEnabled() {
    return this.data.memoryEnabled;
  }

  get writeMode() {
    return WRITE_MODES.find((w) => w.value === this.data.writeMode) || DEFAULT_WRITE_MODE;
  }

  get automaticGuidances() {
    return this.data.automaticGuidances;
  }

  get describeSenses() {
    return this.data.describeSenses;
  }

  get updatedAt() {
    return this.data.updatedAt.toDate();
  }

  get createdAt() {
    return this.data.createdAt.toDate();
  }

  get snippet() {
    return this.data.snippet || "";
  }

  get storyEngineTouched() {
    return this.data.storyEngineTouched;
  }

  get canvasTouched() {
    return this.data.canvasTouched;
  }

  get locked() {
    return this.data.locked;
  }

  get isDuplicating() {
    return this.data.isDuplicating;
  }

  get words() {
    return this.data.words;
  }

  get folders() {
    return this.data.folders || [];
  }

  get showCharacterUpgradeOffer() {
    return this.data.showCharacterUpgradeOffer || false;
  }

  get isStoryBible3() {
    return this.data.isStoryBible3 || false;
  }

  get deferProjectUpgrade() {
    return this.data.deferProjectUpgrade || false;
  }

  get showWorldBuildingSuggestions() {
    if (this.data.showWorldBuildingSuggestions === undefined) {
      return true;
    }
    return this.data.showWorldBuildingSuggestions;
  }

  get importStep() {
    return this.data.importStep || null;
  }

  get spacingCleanupJob() {
    return this.data.spacingCleanupJob || null;
  }

  get importReportDocumentId() {
    return this.data.importReportDocumentId || null;
  }

  get importReportUnavailable() {
    return !!this.data.importReportUnavailable;
  }

  get source() {
    return this.data.source || null;
  }

  get importSource() {
    return this.data.importSource || null;
  }

  get showFeedbackBanner() {
    return this.data.showFeedbackBanner || false;
  }

  get useStructuredOutline() {
    return this.data.useStructuredOutline || false;
  }

  get useScenes() {
    return this.data.useScenes || false;
  }

  get showModelPicker() {
    return this.data.showModelPicker || false;
  }

  get hasMyVoiceTraining() {
    return this.data.hasMyVoiceTraining || false;
  }

  get outlineMigrated() {
    return this.data.outlineMigrated || false;
  }

  get seriesId() {
    return this.data.seriesId || null;
  }

  static list({ userId, deleted = false, callback } = {}) {
    if (!callback) {
      throw new Error("Must specify callback");
    }
    if (!userId) {
      throw new Error("Must specify userId");
    }

    return collection
      .where("userId", "==", userId)
      .where("deleted", "==", deleted)
      .where("archiveDeleted", "==", false) // never make archiveDeleted projects available
      .orderBy("updatedAt", "desc")
      .onSnapshot(async (snap) => {
        let projects = snap.docs.map((doc) => {
          return new Project({ id: doc.id, data: doc.data() });
        });

        // collate metadata to get
        callback(projects);
      });
  }

  static async listAll({ userId } = {}) {
    if (!userId) {
      throw new Error("Must specify userId");
    }

    const snapshot = await collection.where("userId", "==", userId).where("archiveDeleted", "==", false).orderBy("updatedAt", "desc").get();

    return snapshot.docs.map((doc) => {
      return new Project({ id: doc.id, data: doc.data() });
    });
  }

  /**
   * Gets a project with id and returns the object
   * @param {*} id
   * @param {boolean} admin - if true, will return an archive deleted project for admin purposes
   */
  static async get(id, admin = false) {
    // fetch from server
    const doc = await collection.doc(id).get();
    const data = doc.data();

    if (!data) {
      return null;
    }

    // do not make archive deleted projects available
    if (data.archiveDeleted && !admin) {
      return null;
    }

    const metaData = await Project.getMetadata(data.userId, id);

    const project = new Project({ id: id, data: data, metaData: metaData });

    return project;
  }

  static async touch(id) {
    await Auth.fetchWithIdToken(`/api/projects/${id}/touch`, { method: "POST" });
  }

  async startSpacingCleanup({ currentDocumentId, documentIds } = {}) {
    const response = await Auth.fetchWithIdToken(
      `/api/projects/${this.id}/remove-empty-paragraphs`,
      documentIds ? { documentIds } : { currentDocumentId },
    );
    this.mergeData({ spacingCleanupJob: response.spacingCleanupJob });
    this.onChange?.(this);
    return response.spacingCleanupJob;
  }

  getSpacingCleanupStatus(jobId) {
    return Auth.fetchWithIdToken(
      `/api/projects/${this.id}/remove-empty-paragraphs/status`,
      { jobId },
    );
  }

  async dismissSpacingCleanup(jobId) {
    await Auth.fetchWithIdToken(
      `/api/projects/${this.id}/remove-empty-paragraphs/dismiss`,
      { jobId },
    );
    this.mergeData({ spacingCleanupJob: null });
    this.onChange?.(this);
  }

  static async getMetadata(userId, id) {
    // construct the metadata
    const metaDataDocs = (await db.collection("userData").doc(userId).collection("documentMetas").where("projectId", "==", id).get()).docs;
    const metaData = {};
    metaDataDocs.forEach((doc) => {
      metaData[doc.id] = Object.assign({ id: doc.id }, doc.data());
    });

    return metaData;
  }

  static async getFirst(userId) {
    const results = await collection.where("userId", "==", userId).where("deleted", "==", false).orderBy("updatedAt", "desc").limit(1).get();

    if (results.docs.length === 0) {
      // first project hasn't been created yet
      return null;
    }

    const proj = await Project.get(results.docs[0].id);

    return proj;
  }

  /**
   * Refetches data
   */
  async fetch() {
    const hadPendingTitleSave = this._pendingTitleSaves > 0;
    const hadPendingImportDismissalSave = this._pendingImportDismissalSaves > 0;
    const hadPendingCollapseSave = this._pendingFolderCollapseSaves > 0;
    const doc = await collection.doc(this.id).get();
    const metaData = await Project.getMetadata(doc.data().userId, this.id);
    const data = doc.data();

    // Preserve optimistic title if a save was in flight when this fetch started
    // OR if one is still in flight now. Either way, the Firestore doc we read
    // may predate the title PUT's write.
    if (hadPendingTitleSave || this._pendingTitleSaves > 0) {
      data.title = this.data.title;
      data.hasAutomaticTitle = this.data.hasAutomaticTitle;
    }

    // Import-starter generation can trigger a stale snapshot while dismissal is
    // saving. Keep the local dismissal even if that save completes mid-fetch.
    if (hadPendingImportDismissalSave || this._pendingImportDismissalSaves > 0) {
      data.importConversationStartersDismissedForReport = this.data.importConversationStartersDismissedForReport;
    }

    // Same race for folder collapse toggles: a concurrent fetch (e.g. the one
    // updateFolder runs after a rename) can read the doc before an in-flight
    // collapse PATCH lands, snapping just-toggled folders back (SW-10306).
    if ((hadPendingCollapseSave || this._pendingFolderCollapseSaves > 0) && Array.isArray(data.folders)) {
      const localCollapsed = new Map((this.data.folders || []).map((folder) => [folder.id, folder.collapsed]));
      data.folders = data.folders.map((folder) => (localCollapsed.has(folder.id) ? { ...folder, collapsed: localCollapsed.get(folder.id) } : folder));
    }

    this.setData({ data, metaData });

    if (this.onChange) {
      this.onChange(this);
    }
    return true;
  }

  static async create({ title, source, firstDocumentTitle, folderId = undefined, withoutBlankDocument = false }) {
    console.log("[PROJECT create]");

    const result = await Auth.fetchWithIdToken("/api/projects/", {
      title: title,
      first_document_title: firstDocumentTitle,
      source: source,
      withoutBlankDocument,
      folder_id: folderId,
    });

    return result.project;
  }

  /**
   * Performs a soft delete and the project is still accessible for the user.
   * @returns {Promise<Boolean>} Resolves to true if successful
   */
  static async delete(id) {
    console.log("[PROJECT delete]");
    const result = await Auth.fetchWithIdToken(`/api/projects/${id}`, { method: "DELETE" });
    return result;
  }

  /**
   * Restores a project that has been soft deleted
   * @returns {Promise<Boolean>} Resolves to true if successful
   */
  static async restore(id) {
    console.log("[PROJECT restore]");
    const result = await Auth.fetchWithIdToken(`/api/projects/${id}/restore`, { method: "POST" });
    return result;
  }

  /**
   * Performs a soft delete and the project is no longer accessible to the user.
   * @returns {Promise<Boolean>} Resolves to true if successful
   */
  static async archiveDelete(id) {
    console.log("[PROJECT archiveDelete]");
    const result = await Auth.fetchWithIdToken(`/api/projects/${id}/archive`, { method: "DELETE" });
    return result;
  }

  /**
   * Allows an admin to restore an archive deleted project
   * @returns {Promise<Boolean>} Resolves to true if successful
   */
  static async restoreArchiveDeleted(id) {
    console.log("[PROJECT restoreArchiveDeleted]");
    const result = await Auth.fetchWithIdToken(`/api/projects/${id}/restore-archive`, { method: "POST" });
    return result;
  }

  async subscribe(onChange) {
    this.onChange = onChange;
    // just re-running fetch is not great here, but it's the only way to make sure
    // the snapshot data is sync'd with the document meta's correctly.
    this.unsubscribe = collection.doc(this.id).onSnapshot({}, () => {
      if (!this.#snapshotInitialised) {
        this.#snapshotInitialised = true;
        return;
      }

      // Skip snapshot-driven fetches while optimistic deletes are in flight,
      // otherwise background server writes (e.g. updateWordCount) trigger a
      // fetch that overwrites the optimistic state with stale data.
      if (this._pendingOptimisticDeletes > 0) {
        return;
      }

      this.fetch();
    });
  }

  release() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  async setWorldBuildingSuggestionVisibility(value) {
    await this.update({ showWorldBuildingSuggestions: value });
  }

  async update({
    contents, //
    folders,
    title,
    tone,
    automaticGuidances,
    writeMode,
    activeDocument,
    describeSenses,
    memory,
    memoryEnabled,
    deleted,
    showWorldBuildingSuggestions,
    showCharacterUpgradeOffer,
    deferProjectUpgrade,
    showFeedbackBanner,
    useStructuredOutline,
    locked,
    useScenes,
    adminUpdate = false,
    showModelPicker,
    hasAutomaticTitle,
    povTense,
    isMultiplePOV,
    hasMyVoiceTraining,
    importFeedback,
    importConversationStartersDismissedForReport,
    feedbackPreferences,
    touch,
  }) {
    const data = { method: "PUT" };

    if (title !== undefined) {
      data.title = title;
    }
    if (contents !== undefined) {
      data.contents = contents;
    }
    if (folders !== undefined) {
      data.folders = folders;
    }
    if (tone !== undefined) {
      data.tone = TONES.find((t) => t === tone).value;
    }
    if (automaticGuidances !== undefined) {
      data.automaticGuidances = automaticGuidances;
    }
    if (writeMode !== undefined) {
      data.writeMode = WRITE_MODES.find((w) => w === writeMode).value;
    }
    if (activeDocument !== undefined) {
      data.activeDocument = activeDocument;
    }
    if (memory !== undefined) {
      data.memory = memory;
    }
    if (memoryEnabled !== undefined) {
      data.memoryEnabled = memoryEnabled;
    }
    if (deleted !== undefined) {
      data.deleted = deleted;
    }

    if (showWorldBuildingSuggestions !== undefined) {
      data.showWorldBuildingSuggestions = showWorldBuildingSuggestions;
    }

    if (showCharacterUpgradeOffer !== undefined) {
      data.showCharacterUpgradeOffer = showCharacterUpgradeOffer;
    }

    if (deferProjectUpgrade !== undefined) {
      data.deferProjectUpgrade = deferProjectUpgrade;
    }

    if (showFeedbackBanner !== undefined) {
      data.showFeedbackBanner = showFeedbackBanner;
    }

    if (useStructuredOutline !== undefined) {
      data.useStructuredOutline = useStructuredOutline;
    }

    if (describeSenses !== undefined && describeSenses instanceof Object) {
      if (Object.keys(describeSenses).length === 0) {
        // Do not allow setting no senses
        console.log("Cannot set no senses");
        return false;
      }
      data.describeSenses = describeSenses;
    }

    if (locked !== undefined) {
      data.locked = locked;
    }

    if (useScenes !== undefined) {
      data.useScenes = useScenes;
    }

    if (showModelPicker !== undefined) {
      data.showModelPicker = showModelPicker;
    }

    if (povTense !== undefined) {
      data.povTense = povTense;
    }

    if (isMultiplePOV !== undefined) {
      const previousState = normalizeIsMultiplePOVState(this.data?.isMultiplePOV, {
        value: !!this.data?.is_multiple_pov,
        userChanged: !!this.data?.is_user_changed_pov,
      });
      const nextState = normalizeIsMultiplePOVState(isMultiplePOV, previousState);
      data.isMultiplePOV = nextState;
      data.is_multiple_pov = nextState.value;
      data.is_user_changed_pov = nextState.userChanged;
    }

    if (hasMyVoiceTraining !== undefined) {
      data.hasMyVoiceTraining = hasMyVoiceTraining;
    }

    if (hasAutomaticTitle !== undefined) {
      data.hasAutomaticTitle = hasAutomaticTitle;
    }
    if (importConversationStartersDismissedForReport !== undefined) {
      data.importConversationStartersDismissedForReport = importConversationStartersDismissedForReport;
    }
    if (importFeedback !== undefined) {
      data.importFeedback = importFeedback;
    }
    if (feedbackPreferences !== undefined) {
      data.feedbackPreferences = feedbackPreferences;
    }

    // Track title saves so snapshot-driven fetches don't revert the optimistic title.
    const isTitleSave = title !== undefined;
    if (isTitleSave) {
      this._pendingTitleSaves++;
    }

    const isImportDismissalSave = importConversationStartersDismissedForReport !== undefined;
    if (isImportDismissalSave) {
      this._pendingImportDismissalSaves++;
    }

    // optimistically update
    if (this.onChange) {
      this.mergeData(data);
      this.onChange(this);
    }

    const url = adminUpdate ? `/api/projects/${this.id}/admin` : `/api/projects/${this.id}`;

    let decrementedEarly = false;

    const request = async () => {
      let result;
      try {
        result = await Auth.fetchWithIdToken(url, touch === false ? { ...data, touch: false } : data);

        return result;
      } catch (e) {
        // On failure, decrement the counter BEFORE the reconcile fetch so that
        // fetch() allows server truth to overwrite the optimistic values.
        if (isTitleSave) {
          this._pendingTitleSaves--;
        }
        if (isImportDismissalSave) {
          this._pendingImportDismissalSaves--;
        }
        decrementedEarly = true;
        await this.fetch();
        return result;
      }
    };

    return new Promise((resolve, reject) => {
      try {
        this.queue.enqueue(async () => {
          try {
            const result = await request();
            resolve(result);
            return result;
          } catch (error) {
            reject(error);
            throw error;
          } finally {
            if (isTitleSave && !decrementedEarly) {
              this._pendingTitleSaves--;
            }
            if (isImportDismissalSave && !decrementedEarly) {
              this._pendingImportDismissalSaves--;
            }
          }
        });
      } catch (error) {
        if (isTitleSave) {
          this._pendingTitleSaves--;
        }
        if (isImportDismissalSave) {
          this._pendingImportDismissalSaves--;
        }
        reject(error);
      }
    });
  }

  mergeData(data) {
    if (data.updatedAt) {
      if (!(data.updatedAt instanceof Date)) {
        data.updatedAt = new Date(data.updatedAt);
      }
    }

    if (data.folders !== undefined || data.contents !== undefined || data.deletedDocuments !== undefined) {
      this._invalidateTreeNodesCache();
    }

    this.data = Object.assign(this.data, data);

    const multiplePOVState = normalizeIsMultiplePOVState({
      value: this.data?.is_multiple_pov,
      userChanged: this.data?.is_user_changed_pov,
    });
    this.data.isMultiplePOV = multiplePOVState;
    this.data.is_multiple_pov = multiplePOVState.value;
    this.data.is_user_changed_pov = multiplePOVState.userChanged;
  }

  async createDocument({
    content = null,
    title = null,
    previousDocumentId = null,
    povTenseId = null,
    afterDocumentId = null,
    waitForContentsInProject = true,
  } = {}) {
    // don't create another document if already in progress
    if (this.creatingDocument) {
      return;
    }

    this.creatingDocument = true;
    console.log("[PROJECT createDocument]");

    try {
      // Only fetch subscription when caller requests POV/Tense on creation.
      let hasAccessToPOVAndTenseBeta = false;
      if (povTenseId) {
        const subscription = await Subscription.fetchFromUserId(this.data.userId);
        const featureFlags = subscription?.getFeatureFlags?.();
        hasAccessToPOVAndTenseBeta = Boolean(featureFlags?.hasAccessToPOVAndTenseBeta);
      }

      // Create document, then update project state as requested by caller.
      const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents`, {
        title,
        content,
        previousDocumentId,
        ...(hasAccessToPOVAndTenseBeta && povTenseId ? { povTenseId } : {}),
        ...(afterDocumentId ? { afterDocumentId } : {}),
      });

      if (waitForContentsInProject) {
        await this.fetch();

        // The contents array isn't being updated immediately. We wait here when
        // callers require the new doc to exist in `project.contents` first.
        await new Promise((resolve) => {
          const ensureDocumentInProject = () => {
            const found = !!this.contents.find((item) => item.id === result.document.id);
            return found;
          };

          const intervalId = setInterval(() => {
            if (ensureDocumentInProject()) {
              clearInterval(intervalId);
              resolve();
            }
          }, 100);
        });
      } else {
        // Keep UI responsive (e.g. send-to-doc from chat): refresh in background.
        void this.fetch();
      }

      return result;
    } finally {
      this.creatingDocument = false;
    }
  }

  async importDocument(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", this.id);
    const result = await Auth.postFilesWithIdToken("/api/import", {}, formData);
    await this.fetch();
    const docId = result.document.id;
    return docId;
  }

  async updateProjectTitle({ title, hasAutomaticTitle }) {
    this.queue.enqueue(async () => {
      let result;

      try {
        result = await Auth.fetchWithIdToken(`/api/projects/${this.id}`, { title, hasAutomaticTitle, method: "PUT" });
      } catch (e) {
        await this.fetch();
      } finally {
        return result;
      }
    });
  }

  async updateDocument({ id, title, hasAutomaticTitle, previousId, povTenseId }) {
    if (!this.metaData) {
      throw new Error("Project does not have metaData fetched");
    }

    console.log("[PROJECT updateDocument]", { id, title, povTenseId });
    const idx = this.contents.findIndex((item) => item.id === id);

    if (idx === -1) {
      throw new Error("Can't update a document that doesn't exist: " + id);
    }

    // optimistically update
    if (this.onChange && title !== undefined) {
      const newMetaData = produce(this.metaData, (draftState) => {
        draftState[id].title = title;
        draftState[id].hasAutomaticTitle = hasAutomaticTitle || false;
      });

      this.metaData = newMetaData;

      this.onChange(this);
    }

    const updateData = omitBy({ title, hasAutomaticTitle, previousId, povTenseId }, isUndefined);

    this.queue.enqueue(async () => {
      let result;

      try {
        result = await Auth.fetchWithIdToken(`/api/document/update/${id}`, updateData);

        if (this.onChange && updateData.previousId !== undefined) {
          // force a re-fetch of the document data
          // this allows us to pull together all the document data from the various sources
          this.onChange(this, { type: "document_update", documentId: id });
        }
      } catch (e) {
        await this.fetch();
      } finally {
        return result;
      }
    });
  }

  async createFolder(name, parentId, displayOrder) {
    console.log("[PROJECT createFolder]", { name, parentId, displayOrder });

    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/folders`, {
      name,
      parentId,
      displayOrder,
    });

    await this.fetch();

    return result;
  }

  async optInStructuredOutline(force = false) {
    console.log("[PROJECT optInStructuredOutline]");

    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/use-structured-outline`, {
      method: "POST",
      force,
    });

    await this.fetch();

    return result;
  }

  static async convertToScenes(id, force = false) {
    console.log("[PROJECT convertToScenes]", id);
    const result = await Auth.fetchWithIdToken(`/api/ai/convertProjectToScenes`, {
      method: "POST",
      projectId: id,
      force,
    });
    return result;
  }

  static async cancelSceneConversion(id) {
    console.log("[PROJECT cancelSceneConversion]", id);
    const result = await Auth.fetchWithIdToken(`/api/projects/${id}/cancel-convert-to-scenes`, {
      method: "POST",
    });
    return result;
  }

  async updateFolder(id, { name, collapsed }) {
    console.log("[PROJECT updateFolder]", { id, name, collapsed });

    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/folders/${id}`, {
      name,
      collapsed,
    });

    await this.fetch();

    return result;
  }

  async toggleFolderCollapsed(id) {
    // Read the current flag here — tree-node callers hold stale copies (SW-10306).
    const collapsed = !((this.data.folders || []).find((folder) => folder.id === id)?.collapsed ?? false);
    // Optimistic update; unlike updateFolder, no full re-fetch after the save.
    this.data.folders = (this.data.folders || []).map((folder) => (folder.id === id ? { ...folder, collapsed } : folder));
    if (this.onChange) {
      this.onChange(this);
    }

    this._pendingFolderCollapseSaves++;

    // The serial queue keeps rapid toggles from racing each other server-side.
    // Never rejects: failures are handled here (revert + resync), and the only
    // caller is a fire-and-forget click handler.
    return new Promise((resolve) => {
      this.queue.enqueue(async () => {
        try {
          const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/folders/${id}`, { collapsed });
          resolve(result);
          this._pendingFolderCollapseSaves--;
        } catch (e) {
          resolve(null);
          this._pendingFolderCollapseSaves--;
          // Best-effort revert of optimistic update. fetch() restores server
          // truth while preserving any newer in-flight toggles.
          try {
            await this.fetch();
          } catch (_) {
            // fetch failed too — optimistic state stays until next snapshot sync
          }
        }
      });
    });
  }

  async deleteFolder(id) {
    console.log("[PROJECT deleteFolder]", { id });

    await Auth.fetchWithIdToken(`/api/projects/${this.id}/folders/${id}`, {
      method: "DELETE",
    });

    await this.fetch();
  }

  manipulateTree(nodeId, { parentId, displayOrder }) {
    const oldNodes = this.getAllTreeNodes();

    const nodes = oldNodes
      .map((n) => {
        if (n.id === nodeId) {
          return { ...n, parentId, displayOrder };
        }
        return n;
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const top = nodes.filter((n) => n.parentId === "none");

    top.forEach((n) => this.buildTree(n, nodes));
    return top;
  }

  async updateTree(id, { parentId, displayOrder }) {
    console.log("[PROJECT updateTree]", { id, parentId, displayOrder });

    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/tree`, {
      itemId: id,
      parentId: parentId,
      displayOrder: displayOrder,
    });

    await this.fetch();

    return result;
  }

  buildTree(node, nodes) {
    if (node.type === "document") {
      return node;
    }
    const children = nodes.filter((n) => n.parentId === node.id);
    children.forEach((child) => this.buildTree(child, nodes));
    node.children = children;
    return node;
  }

  _invalidateTreeNodesCache() {
    this._treeNodesCache = null;
    this._treeNodesCacheFolders = null;
    this._treeNodesCacheDocuments = null;
  }

  getAllTreeNodes() {
    const folders = this.folders || [];
    const documents = this.data.contents?.contents || [];

    if (
      this._treeNodesCache &&
      this._treeNodesCacheFolders === folders &&
      this._treeNodesCacheDocuments === documents
    ) {
      return this._treeNodesCache;
    }

    const folderCount = folders.length;
    const documentCount = documents.length;
    const nodes = new Array(folderCount + documentCount);

    for (let i = 0; i < folderCount; i++) {
      const f = folders[i];
      nodes[i] = {
        type: "folder",
        id: f.id,
        name: f.name,
        parentId: f.parentId || "none",
        displayOrder: f.displayOrder,
        collapsed: f.collapsed,
        toggleCollapsed: () => this.toggleFolderCollapsed(f.id),
      };
    }

    for (let i = 0; i < documentCount; i++) {
      const d = documents[i];
      nodes[folderCount + i] = {
        type: "document",
        id: d.id,
        name: d.title,
        parentId: d.parentId || "none",
        displayOrder: d.displayOrder,
      };
    }

    nodes.sort((a, b) => a.displayOrder - b.displayOrder);

    this._treeNodesCache = nodes;
    this._treeNodesCacheFolders = folders;
    this._treeNodesCacheDocuments = documents;
    return nodes;
  }

  getMaxDisplayOrder(parentId) {
    const children = this.getChildren(parentId);
    if (children.length === 0) return 1;
    return Math.ceil(Math.max(...children.map((n) => n.displayOrder)) || 1);
  }

  getRootTree() {
    const nodes = this.getAllTreeNodes();
    const top = nodes.filter((n) => !n.parentId || n.parentId === "none");

    top.forEach((n) => this.buildTree(n, nodes));
    return top;
  }

  getChildren(parentId) {
    const nodes = this.getAllTreeNodes();
    const children = nodes.filter((n) => n.parentId === parentId);
    return children;
  }

  /**
   * Count non-trashed documents in a folder subtree (including nested folders).
   * Walks folder/document lists directly — does not build or sort the full tree.
   */
  getDocumentCountInFolder(folderId) {
    const folders = this.folders || [];
    if (!folders.some((folder) => folder.id === folderId)) {
      return 0;
    }

    const childFolderIdsByParent = new Map();
    for (const folder of folders) {
      const parentId = folder.parentId || "none";
      const siblings = childFolderIdsByParent.get(parentId);
      if (siblings) {
        siblings.push(folder.id);
      } else {
        childFolderIdsByParent.set(parentId, [folder.id]);
      }
    }

    const subtreeFolderIds = new Set([folderId]);
    const stack = [folderId];
    while (stack.length > 0) {
      const currentId = stack.pop();
      const childFolderIds = childFolderIdsByParent.get(currentId);
      if (!childFolderIds) continue;
      for (const childFolderId of childFolderIds) {
        if (!subtreeFolderIds.has(childFolderId)) {
          subtreeFolderIds.add(childFolderId);
          stack.push(childFolderId);
        }
      }
    }

    const deletedIds = new Set((this.data.deletedDocuments || []).map((doc) => doc.id));
    const documents = this.data.contents?.contents || [];
    let count = 0;
    for (const document of documents) {
      if (deletedIds.has(document.id)) continue;
      const parentId = document.parentId || "none";
      if (subtreeFolderIds.has(parentId)) {
        count++;
      }
    }
    return count;
  }

  getDirectAncestors(nodeId) {
    if (!nodeId || nodeId === "none") return [];
    const nodes = this.getAllTreeNodes();
    const node = nodes.find((n) => n.id === nodeId);
    const ancestors = [node.id];
    let current = node;
    while (current.parentId !== "none") {
      let parentNode = null;
      for (const candidate of nodes) {
        if (candidate.id === current.parentId) {
          parentNode = candidate;
          break;
        }
      }

      if (!parentNode) {
        break;
      }

      current = parentNode;
      ancestors.push(current.id);
    }
    return ancestors;
  }

  /**
   * First document in the left sidebar order (depth-first, displayOrder at each level).
   * Used when opening a project without a #doc= hash so the editor matches the document list.
   */
  getFirstDocumentIdInSidebarOrder() {
    const rootTree = this.getRootTree();

    const firstDocumentId = (nodes) => {
      if (!nodes?.length) return null;
      const sortedNodes = [...nodes].sort((a, b) => (parseFloat(a.displayOrder) || 0) - (parseFloat(b.displayOrder) || 0));

      for (const node of sortedNodes) {
        if (node.type === "document") return node.id;
        if (node.children?.length) {
          const id = firstDocumentId(node.children);
          if (id) return id;
        }
      }
      return null;
    };

    return firstDocumentId(rootTree);
  }

  /**
   * Returns a flattened array of document nodes in proper hierarchical order,
   * respecting the displayOrder at each level
   * @param {string} excludeDocumentId - Optional document ID to exclude from the result
   * @returns {Array} Array of document objects with label and value properties
   */
  getFlattenedDocuments(excludeDocumentId = null) {
    if (!this.metaData) {
      throw new Error("Project does not have metaData fetched");
    }

    // Get the root tree with folders and documents
    const rootTree = this.getRootTree();

    // Recursive function to collect documents in correct order
    const collectDocuments = (nodes, result = []) => {
      // Sort nodes at this level by display order
      const sortedNodes = [...nodes].sort((a, b) => a.displayOrder - b.displayOrder);

      for (const node of sortedNodes) {
        if (node.type === "document" && node.id !== excludeDocumentId) {
          // Use metadata to get the correct document title
          const docMetadata = this.metaData[node.id];
          const title = docMetadata ? docMetadata.title : node.name;

          // Add document to result
          result.push({ label: title, value: node.id });
        }

        // Process children after the current node (depth-first traversal)
        if (node.children && node.children.length > 0) {
          collectDocuments(node.children, result);
        }
      }

      return result;
    };

    return collectDocuments(rootTree);
  }

  async deleteDocument(id) {
    if (this.onChange) {
      const idx = this.data.contents.contents.findIndex((item) => item.type === "document" && item.id === id);

      const newContent = produce(this.data.contents, (draftState) => {
        draftState.contents.splice(idx, 1);
      });

      this.data.contents = newContent;

      this.onChange(this);
    }

    this._pendingOptimisticDeletes++;

    return new Promise((resolve, reject) => {
      this.queue.enqueue(async () => {
        try {
          const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/${id}`, {
            method: "DELETE",
          });

          resolve(result);
        } catch (e) {
          reject(e);
          // Best-effort revert of optimistic update
          try {
            await this.fetch();
          } catch (_) {
            // fetch failed too — optimistic state stays until next snapshot sync
          }
        } finally {
          this._pendingOptimisticDeletes--;
          // Re-sync with server once all optimistic deletes have settled
          if (this._pendingOptimisticDeletes === 0) {
            await this.fetch();
          }
        }
      });
    });
  }

  async undeleteDocument(id) {
    console.log("[PROJECT undeleteDocument]", { id });

    this.queue.enqueue(async () => {
      let result;

      try {
        result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/${id}/undelete`);
        await this.fetch();

        return result;
      } catch (e) {
        await this.fetch();
        return result;
      }
    });
  }

  async duplicateDocument(id) {
    console.log("[PROJECT duplicateDocument]", id);
    const idx = this.contents.findIndex((item) => item.id === id);

    if (idx === -1) {
      throw new Error("Can't duplicate a document that doesn't exist: " + id);
    }

    try {
      const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/${id}/duplicate`);
      await this.fetch();
      return result;
    } catch (e) {
      await this.fetch();
    }
  }

  async exportDocument(id, title) {
    // Start the export job
    const response = await Auth.fetchWithIdToken("/api/export", {
      projectId: this.id,
      documentId: id,
      documentTitle: title,
    });

    const jobId = response?.jobId;
    if (!jobId) {
      const errorMessage = response?.errorMessage || response?.error || "Failed to start document export.";
      throw new Error(errorMessage);
    }

    // Wait for the export to complete using the worker-backed flow
    const status = await Project.waitForExportJob(jobId, `${title}.docx`);
    const blob = await Project.fetchExportBlob(jobId);
    Project.triggerBlobDownload(blob, status.filename || `${title}.docx`);
    return true;
  }

  static async exportProject(id, title, onProgress = null, onJobId = null) {
    let response;
    try {
      response = await Auth.fetchWithIdToken("/api/export-project", {
        projectId: id,
      });
    } catch (error) {
      // If validation failed, the API returns 400 with error message in the response body
      if (error.response && error.status === 400) {
        let errorMessage = "Failed to queue export job.";

        try {
          const errorData = await error.response.clone().json();
          errorMessage = errorData.errorMessage || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse JSON, try to get the error as text
          try {
            const errorText = await error.response.clone().text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Keep default error message
          }
        }

        throw new Error(errorMessage);
      }
      throw error;
    }

    const jobId = response?.jobId;
    if (!jobId) {
      const errorMessage = response?.errorMessage || response?.error || "Failed to queue export job.";
      throw new Error(errorMessage);
    }

    // Call jobId callback if provided so caller can track the job
    if (onJobId) {
      onJobId(jobId);
    }

    const status = await Project.waitForExportJob(jobId, `${title}.zip`, onProgress);
    const blob = await Project.fetchExportBlob(jobId);
    Project.triggerBlobDownload(blob, status.filename || `${title}.zip`);
    return true;
  }

  static async exportMergedProject(id, title, onProgress = null, onJobId = null) {
    let response;
    try {
      response = await Auth.fetchWithIdToken("/api/export-merged-project", {
        projectId: id,
      });
    } catch (error) {
      // If validation failed, the API returns 400 with error message in the response body
      if (error.response && error.status === 400) {
        let errorMessage = "Failed to queue export job.";

        try {
          const errorData = await error.response.clone().json();
          errorMessage = errorData.errorMessage || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse JSON, try to get the error as text
          try {
            const errorText = await error.response.clone().text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Keep default error message
          }
        }

        throw new Error(errorMessage);
      }
      throw error;
    }

    const jobId = response?.jobId;
    if (!jobId) {
      const errorMessage = response?.errorMessage || response?.error || "Failed to queue export job.";
      throw new Error(errorMessage);
    }

    // Call jobId callback if provided so caller can track the job
    if (onJobId) {
      onJobId(jobId);
    }

    const status = await Project.waitForExportJob(jobId, `${title}-merged.docx`, onProgress);
    const blob = await Project.fetchExportBlob(jobId);
    Project.triggerBlobDownload(blob, status.filename || `${title}-merged.docx`);
    return true;
  }

  static async exportFolder(projectId, folderId, folderName, onProgress = null, onJobId = null) {
    let response;
    try {
      response = await Auth.fetchWithIdToken("/api/export-folder", {
        projectId,
        folderId,
        folderName,
      });
    } catch (error) {
      if (error.response && error.status === 400) {
        let errorMessage = "Failed to queue export job.";
        try {
          const errorData = await error.response.clone().json();
          errorMessage = errorData.errorMessage || errorData.error || errorMessage;
        } catch (parseError) {
          try {
            const errorText = await error.response.clone().text();
            if (errorText) errorMessage = errorText;
          } catch (textError) {}
        }
        throw new Error(errorMessage);
      }
      throw error;
    }

    const jobId = response?.jobId;
    if (!jobId) {
      const errorMessage = response?.errorMessage || response?.error || "Failed to queue export job.";
      throw new Error(errorMessage);
    }

    if (onJobId) onJobId(jobId);

    const status = await Project.waitForExportJob(jobId, `${folderName}.zip`, onProgress);
    const blob = await Project.fetchExportBlob(jobId);
    Project.triggerBlobDownload(blob, status.filename || `${folderName}.zip`);
    return true;
  }

  static async exportMergedFolder(projectId, folderId, folderName, onProgress = null, onJobId = null) {
    let response;
    try {
      response = await Auth.fetchWithIdToken("/api/export-merged-folder", {
        projectId,
        folderId,
        folderName,
      });
    } catch (error) {
      if (error.response && error.status === 400) {
        let errorMessage = "Failed to queue export job.";
        try {
          const errorData = await error.response.clone().json();
          errorMessage = errorData.errorMessage || errorData.error || errorMessage;
        } catch (parseError) {
          try {
            const errorText = await error.response.clone().text();
            if (errorText) errorMessage = errorText;
          } catch (textError) {}
        }
        throw new Error(errorMessage);
      }
      throw error;
    }

    const jobId = response?.jobId;
    if (!jobId) {
      const errorMessage = response?.errorMessage || response?.error || "Failed to queue export job.";
      throw new Error(errorMessage);
    }

    if (onJobId) onJobId(jobId);

    const status = await Project.waitForExportJob(jobId, `${folderName}-merged.docx`, onProgress);
    const blob = await Project.fetchExportBlob(jobId);
    Project.triggerBlobDownload(blob, status.filename || `${folderName}-merged.docx`);
    return true;
  }

  static async waitForExportJob(jobId, defaultFilename, onProgress = null) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      let intervalId = null;

      const checkStatus = async () => {
        try {
          let status;
          try {
            status = await Auth.fetchWithIdToken(`/api/export-project/${jobId}/status`, {
              method: "POST",
            });
          } catch (error) {
            if (error?.status === 404 && intervalId) {
              clearInterval(intervalId);
              reject(new ExportCancelledError("Export cancelled or job no longer exists."));
              return;
            }
            if (intervalId) {
              clearInterval(intervalId);
            }
            reject(error);
            return;
          }

          if (status.cancelRequested) {
            if (intervalId) {
              clearInterval(intervalId);
            }
            reject(new ExportCancelledError("Export cancelled"));
            return;
          }

          // Call progress callback if provided
          if (onProgress && typeof status.progress === 'number') {
            onProgress(status.progress);
          }

          if (status.state === "completed" && status.downloadReady) {
            if (intervalId) {
              clearInterval(intervalId);
            }
            resolve(status);
            return;
          }

          if (status.state === "failed") {
            if (intervalId) {
              clearInterval(intervalId);
            }
            const error = status.error || "Export job failed. Please try again.";
            if (status.cancelRequested || error?.toLowerCase?.().includes("cancelled")) {
              reject(new ExportCancelledError(error));
            } else {
              reject(new Error(error));
            }
            return;
          }

          if (Date.now() - started > EXPORT_JOB_TIMEOUT_MS) {
            if (intervalId) {
              clearInterval(intervalId);
            }
            reject(new Error(`Export is taking longer than expected. Please try again later${defaultFilename ? ` for ${defaultFilename}` : ""}.`));
            return;
          }
        } catch (error) {
          if (intervalId) {
            clearInterval(intervalId);
          }
          reject(error);
        }
      };

      // Run immediately, then set up interval
      checkStatus();
      intervalId = setInterval(checkStatus, EXPORT_JOB_POLL_INTERVAL_MS);
    });
  }

  static async fetchExportBlob(jobId) {
    return Auth.fetchFileWithIdToken(`/api/export-project/${jobId}/download`, {
      method: "POST",
    });
  }

  static triggerBlobDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  static async cancelExport(jobId) {
    return Auth.fetchWithIdToken(`/api/export-project/${jobId}/cancel`, {
      method: "POST",
    });
  }

  async exportCharacters(id) {
    const blob = await Auth.fetchFileWithIdToken("/api/export-characters", { projectId: id });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${this.title} Characters.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async exportWorldbuilding(id) {
    const blob = await Auth.fetchFileWithIdToken("/api/export-worldbuilding", { projectId: id });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${this.title} Worldbuilding.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  static async duplicateProject(id) {
    console.log("[PROJECT duplicateProject]", id);
    return await Auth.fetchWithIdToken(`/api/projects/${id}/duplicate`);
  }

  static async uploadCover(id, { mimeType, base64 }) {
    return await Auth.fetchWithIdToken(`/api/projects/${id}/cover/upload`, { mimeType, base64 });
  }

  static async removeCover(id) {
    return await Auth.fetchWithIdToken(`/api/projects/${id}/cover`, { method: "DELETE" });
  }

  async reorder(from, to) {
    console.log("[PROJECT reorder]");
    // optimistically update
    const newContents = produce(this.data.contents, (draftState) => {
      draftState.contents = arrayMove(this.data.contents.contents, from, to);
    });

    this.data.contents = newContents;

    if (this.onChange) {
      // reorder on client
      this.onChange(this);
    }

    this.queue.enqueue(async () => {
      let result;
      try {
        result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/reorder`, {
          from: from,
          to: to,
          method: "POST",
        });

        return result;
      } catch (e) {
        await this.fetch();
        return result;
      }
    });
  }

  setData({ data, metaData }) {
    this._invalidateTreeNodesCache();
    this.data = data;
    const multiplePOVState = normalizeIsMultiplePOVState({
      value: this.data?.is_multiple_pov,
      userChanged: this.data?.is_user_changed_pov,
    });
    this.data.isMultiplePOV = multiplePOVState;
    this.data.is_multiple_pov = multiplePOVState.value;
    this.data.is_user_changed_pov = multiplePOVState.userChanged;
    this.metaData = metaData;
    this.fetched = true;
  }

  get contents() {
    if (!this.metaData) {
      throw new Error("Project does not have metaData fetched");
    }

    if (this.data.contents && this.data.contents.contents) {
      // imbue contents with titles from metaData
      const out = JSON.parse(JSON.stringify(this.data.contents));

      out.contents.forEach((doc, i) => {
        const metadata = this.metaData[doc.id];

        if (metadata) {
          out.contents[i].title = metadata.title;
          out.contents[i].hasAutomaticTitle = metadata.hasAutomaticTitle || false;
        }
      });

      return out.contents;
    } else {
      return null;
    }
  }

  get deletedDocuments() {
    if (!this.metaData) {
      throw new Error("Project does not have metaData fetched");
    }

    if (this.data.deletedDocuments && Array.isArray(this.data.deletedDocuments)) {
      const out = JSON.parse(JSON.stringify(this.data.deletedDocuments));

      out.forEach((doc, i) => {
        if (!doc?.id) return;
        const metadata = this.metaData[doc.id];
        if (metadata) {
          out[i].title = metadata.title;
        }
      });

      return out;
    } else {
      return [];
    }
  }

  async fetchDocumentTitle(text) {
    const result = await Auth.fetchWithIdToken("/api/ai/documentTitle", { text });
    return result;
  }

  documentIsUntitled(docId) {
    const idx = this.contents.findIndex((item) => item.id === docId);

    if (idx === -1) {
      return null;
    }

    const regex = new RegExp(/^Untitled/);
    return regex.test(this.contents[idx].title);
  }

  async automaticallyTitleDocument(text, documentId) {
    // if we're already fetching or already fetched a title for this doc, return that
    if (this.automaticDocumentTitlingMemo[documentId]) {
      return this.automaticDocumentTitlingMemo[documentId];
    }

    // otherwise, make a new fetch and memoize it
    this.automaticDocumentTitlingMemo[documentId] = (async () => {
      try {
        const result = await this.fetchDocumentTitle(text);

        // if the user titled the doc while we were fetching, don't make updates
        // (could also use an AbortController for this)
        if (result.text && this.documentIsUntitled(documentId)) {
          // updates document title in documentMetas sub-collection (in userData)
          await this.updateDocument({
            id: documentId,
            title: result.text,
            hasAutomaticTitle: true,
          });
        }

        return result;
      } catch (e) {
        console.log(`Couldn't title document: ${documentId}`);
      }
    })();

    return this.automaticDocumentTitlingMemo[documentId];
  }

  async automaticallyTitleProject(text) {
    // if we're already fetching or already fetched a title for this project, return that
    if (this.automaticProjectTitlingMemo) {
      return this.automaticProjectTitlingMemo;
    }

    // otherwise, make a new fetch and memoize it
    this.automaticProjectTitlingMemo = (async () => {
      try {
        const result = await this.fetchDocumentTitle(text);

        // if the user titled the project while we were fetching, don't make updates
        // (could also use an AbortController for this)
        if (result.text && this.isUntitled()) {
          // updates project title
          await this.updateProjectTitle({
            title: result.text,
            hasAutomaticTitle: true,
          });
        }

        return result;
      } catch (e) {
        Sentry.captureException(e, { tags: { projectId: this.projectId } });
      }
    })();

    return this.automaticProjectTitlingMemo;
  }

  // use the first automatically titled doc to for the project
  generateProjectTitle() {
    const firstTitledDoc = this.contents.find((item) => item.hasAutomaticTitle);

    // return title if we have one, otherwise return undefined
    return firstTitledDoc && firstTitledDoc.title;
  }

  isUntitled() {
    const untitledRegex = new RegExp(/^Untitled$/);
    const projectRegex = new RegExp(/^Project \d+$/);
    return untitledRegex.test(this.title) || projectRegex.test(this.title);
  }

  /**
   * Determines if a project is "new" based on creation time
   * A project is considered new if it was created within the last 2 minutes
   * @returns {boolean} True if the project is new
   */
  isNew() {
    // Check if project was created within the last 2 minutes
    const now = new Date();
    const createdAt = new Date(this.createdAt);
    const twoMinutesInMs = 2 * 60 * 1000;
    const isRecentlyCreated = now - createdAt < twoMinutesInMs;

    if (!isRecentlyCreated) {
      return false;
    }
    return true;
  }

  /**
   * Deletes all the deleted documents in the project
   * @returns {Promise<Number>} Number of documents deleted
   */
  async emptyTrash() {
    console.log("[PROJECT emptyTrash]", { id: this.id });

    // may need to optimistically update this.deletedDocuments here if there is an onChange

    // do we need to enqueue this request?
    let result;

    try {
      result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/`, {
        method: "DELETE",
      });

      await this.fetch();

      return result.documentsDeleted;
    } catch (e) {
      await this.fetch();
      return result;
    }
  }

  /**
   * Get a chunk of Project snapshots
   * use the last response snapshot as startAfter parameter for subsequent calls
   * @param userId
   * @param limit
   * @param startAfter
   * @returns {Promise<*[]>}
   */
  static async getListAfter({ userId, limit = 5, startAfter = null }) {
    const result = [];
    const baseQuery = collection
      .where("userId", "==", userId)
      .where("deleted", "==", false)
      .where("archiveDeleted", "==", false) // never make archiveDeleted projects available
      .orderBy("updatedAt", "desc")
      .limit(limit);
    const response = await (startAfter ? baseQuery.startAfter(startAfter).get() : baseQuery.get());
    response.forEach((doc) => result.push(doc));
    return result;
  }

  async getPreviousDocumentIdFromChapter({ chapterId, chapters, scenes, userId }) {
    const metaData = await Project.getMetadata(userId, this.id);
    const currentChapterIdx = chapters.findIndex((chapter) => chapter.value === chapterId);
    const previousChapters = chapters.slice(0, currentChapterIdx);

    let previousDocumentId;
    for (let i = previousChapters.length - 1; i >= 0; i--) {
      const chapter = previousChapters[i];
      const linkedScenes = Object.values(scenes).filter((scene) => {
        return scene.itemId === chapter.value && !this.deletedDocuments?.some((doc) => doc.id === scene.contents.id);
      });
      const documentIds = linkedScenes.map((scene) => scene.contents.id);

      if (documentIds.length === 0) {
        // there are no documents linked to this chapter, keep checking previous chapters
        continue;
      }

      if (documentIds.length === 1) {
        // there is only one document linked to this chapter, so we can use it as the previousDocumentId
        previousDocumentId = documentIds[0];
        break;
      }

      // Multiple documents found - get the most recently created one
      // note: we're using updatedAt, but this is actually createdAt, since it is never updated
      const documentsWithDates = documentIds.map((id) => ({
        documentId: id,
        updatedAt: metaData[id]?.updatedAt,
      }));

      const mostRecent = documentsWithDates.reduce((latest, current) => {
        if (!latest.updatedAt || (current.updatedAt && current.updatedAt > latest.updatedAt)) {
          return current;
        }
        return latest;
      });

      previousDocumentId = mostRecent.documentId;
      break;
    }

    return previousDocumentId;
  }

  async getPOVTense() {
    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/povTense`);
    return result;
  }

  async getDocumentPOVTense(documentId) {
    const result = await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/${documentId}/povTense`);
    return result;
  }

  async setDocumentPOVTense({ documentId, povTenseId, userId }) {
    if (!userId) {
      return;
    }

    const subscription = await Subscription.fetchFromUserId(userId);
    const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
    if (!hasAccessToPOVAndTenseBeta) {
      return;
    }

    return await Auth.fetchWithIdToken(`/api/projects/${this.id}/documents/${documentId}/povTense`, {
      povTenseId,
    });
  }

  async updateDocumentPOVTense({ povTenseId, povTense, userId }) {
    if (!userId) {
      return;
    }

    const subscription = await Subscription.fetchFromUserId(userId);
    const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
    if (!hasAccessToPOVAndTenseBeta) {
      return;
    }

    return await Auth.fetchWithIdToken(`/api/povTense/${povTenseId}`, {
      method: "PUT",
      povTense,
    });
  }

  async setIsMultiplePOV(isMultiplePOV, userId, previousStateOverride = null) {
    if (!userId) {
      return;
    }

    const currentState =
      previousStateOverride !== null
        ? normalizeIsMultiplePOVState(previousStateOverride)
        : normalizeIsMultiplePOVState(this.data?.isMultiplePOV, {
            value: !!this.data?.is_multiple_pov,
            userChanged: !!this.data?.is_user_changed_pov,
          });
    const nextState = normalizeIsMultiplePOVState(isMultiplePOV, currentState);

    const subscription = await Subscription.fetchFromUserId(userId);
    const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
    if (!hasAccessToPOVAndTenseBeta) {
      return;
    }

    return await this.update({ isMultiplePOV: nextState });
  }

  async setPOVTense({ pov, tense }) {
    // For now, only detect POV and tense if the user has access to beta
    const subscription = await Subscription.fetchFromUserId(this.data.userId);
    const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
    if (!hasAccessToPOVAndTenseBeta) {
      return;
    }

    await this.update({ povTense: { pov, tense } });
  }

  async detectPOVTense(textSnippet, userId, genres = "") {
    let povTense;

    // For now, only detect POV and tense if the user has access to beta
    const subscription = await Subscription.fetchFromUserId(userId);
    const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
    if (!hasAccessToPOVAndTenseBeta) {
      return;
    }

    // Get the latest POV and tense from the server
    const currentPOVTense = await this.getPOVTense();

    // If the text snippet is empty, return early and use the default POV and tense
    if (!textSnippet) {
      return;
    } else {
      const userChangedPOV = currentPOVTense?.povTense?.pov?.userChanged;
      const userChangedTense = currentPOVTense?.povTense?.tense?.userChanged;
      const multiplePOVState = normalizeIsMultiplePOVState(currentPOVTense?.isMultiplePOV);
      if (userChangedPOV && userChangedTense) return;
      if (multiplePOVState.value) return;

      try {
        const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/povAndTense", { textSnippet, genres });
        const { pov, tense } = response;

        // Since the AI can return invalid POV and tense values, we need to check if they are valid and use the current POV and tense if they are not
        // It's not a critical feature, so we'll just use the current POV and tense if the AI returns invalid values
        const povValue = POVTense.isValidPovValue(pov) && !userChangedPOV ? pov : currentPOVTense?.povTense?.pov?.value;
        const tenseValue = POVTense.isValidTenseValue(tense) && !userChangedTense ? tense : currentPOVTense?.povTense?.tense?.value;

        povTense = {
          pov: { value: povValue, characters: currentPOVTense?.povTense?.pov?.characters || [], userChanged: userChangedPOV },
          tense: { value: tenseValue, userChanged: userChangedTense },
        };
      } catch (error) {
        if (error.status === 500) {
          povTense = createPOVTenseObject({ ...DEFAULT_POV_TENSE_OBJECT.pov, characters: currentPOVTense?.povTense?.pov?.characters || [] });
        }

        Sentry.captureMessage("Unable to generate POV and tense for the Story Bible", {
          tags: { projectId: this.id, textSnippet },
        });
      }
    }

    this.update({ povTense });
    return povTense;
  }

  static async getFlags(projectId) {
    const flags = await Auth.fetchWithIdToken(`/api/projects/${projectId}/flags`);
    return flags;
  }
}

export function projectUrlId(id) {
  return `/app/project/${id}`;
}

export function projectUrl(project) {
  return projectUrlId(project.id);
}
